import { useEffect, useState } from "react";
import moment, { type Moment } from "moment";
import { Button, Select } from "@rio-cloud/rio-uikit";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import TableCol from "@rio-cloud/rio-uikit/TableCol";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";
import type { SelectOption } from "@rio-cloud/rio-uikit";
import DialogAudit from "@components/Dialog/Audit";
import { useIntl } from "react-intl";
import { useT } from "@hooks";
import { searchAuditTransactions } from "@services/audit";
import { getTransactionStatusLabelKey, resolveTransactionStatus } from "@utils/transactionStatus";
import type { AuditModule, AuditTransactionListItem, AuditTransactionsSearchFilters } from "../../../../types/audit";

const PAGE_SIZE = 10;

const COLUMNS = ["module", "category", "createdAt", "updatedAt", "status", "history"] as const;

const SKELETON_CELL_HEIGHT = 20;
const SKELETON_ACTION_HEIGHT = 34;
const SKELETON_ACTION_WIDTH = 90;
const SKELETON_CELL_WIDTHS: Record<string, string> = {
  module: "70%",
  category: "85%",
  createdAt: "55%",
  updatedAt: "55%",
  status: "62%",
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  previsto: "badge bg-lightest border border-color-lighter",
  realizado: "badge badge-soft-success",
  vencido: "badge badge-soft-danger",
};

const MODULE_LABEL_KEY: Record<AuditModule, string> = {
  DESPESA: "settings.audit.module.expense",
  RECEITA: "settings.audit.module.receipt",
};

const toApiDate = (value: Moment | string): string | undefined => {
  if (!value) return undefined;

  const parsed = typeof value === "string" ? moment(value, "DD/MM/YYYY", true) : value;
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

const formatDate = (value?: string | null): string => {
  const parsed = moment(value);
  return value && parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
};

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const AuditPage = () => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);

  const [module, setModule] = useState("");
  const [startDate, setStartDate] = useState<Moment | string>("");
  const [endDate, setEndDate] = useState<Moment | string>("");
  const [filters, setFilters] = useState<AuditTransactionsSearchFilters>({});
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<AuditTransactionListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<AuditTransactionListItem | null>(null);

  const MODULE_OPTIONS: SelectOption[] = [
    { id: "DESPESA", label: t(MODULE_LABEL_KEY.DESPESA) },
    { id: "RECEITA", label: t(MODULE_LABEL_KEY.RECEITA) },
  ];

  const columnLabels: Record<string, string> = {
    module: t("settings.audit.column.module"),
    category: t("settings.audit.column.category"),
    createdAt: t("settings.audit.column.createdAt"),
    updatedAt: t("settings.audit.column.updatedAt"),
    status: t("settings.audit.column.status"),
    history: t("settings.audit.column.history"),
  };

  useEffect(() => {
    let isActive = true;

    const loadAuditTransactions = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const response = await searchAuditTransactions({ ...filters, page, size: PAGE_SIZE });

        if (!isActive) return;

        setRows(response.content);
        setTotalPages(Math.max(1, response.totalPages));
      } catch {
        if (!isActive) return;

        setRows([]);
        setTotalPages(1);
        setHasError(true);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadAuditTransactions();

    return () => {
      isActive = false;
    };
  }, [filters, page]);

  const progressWidth = `${((page + 1) / totalPages) * 100}%`;

  const handleApplyFilter = () => {
    setPage(0);
    setFilters({
      type: module ? (module as AuditModule) : undefined,
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
    });
  };

  return (
    <div className="display-flex flex-column gap-5 min-width-0">
      <span className="text-bold text-size-20">{t("settings.audit.title")}</span>
      <span className="text-normal text-size-14">{t("settings.audit.subtitle")}</span>

      <div className="display-flex flex-wrap align-items-end gap-15 margin-top-20">
        <div className="display-flex flex-column width-100pct-xs width-250">
          <label htmlFor="auditModule">{t("settings.audit.label.module")}</label>
          <Select
            id="auditModule"
            name="auditModule"
            options={MODULE_OPTIONS}
            value={module ? [module] : []}
            placeholder={t("settings.audit.placeholder.module")}
            onChange={(option) => setModule(option?.id ?? "")}
            useClear
          />
        </div>

        <div className="display-flex flex-column width-100pct-xs width-150">
          <label htmlFor="auditStartDate">{t("settings.audit.label.updatedAt")}</label>
          <DatePicker
            id="auditStartDate"
            dateFormat="DD/MM/YYYY"
            className="rounded-small margin-bottom-0"
            locale={momentLocale}
            timeFormat={false}
            value={startDate}
            onChange={(value) => setStartDate(value)}
            inputProps={{ placeholder: t("common.placeholder.selectDate") }}
          />
        </div>

        <div className="display-flex flex-column">
          <span className="display-none display-block-sm margin-bottom-5" aria-hidden="true">
            &nbsp;
          </span>
          <span className="display-flex align-items-center height-35 text-size-14">{t("settings.audit.label.to")}</span>
        </div>

        <div className="display-flex flex-column width-100pct-xs width-150">
          <span className="display-none display-block-sm margin-bottom-5" aria-hidden="true">
            &nbsp;
          </span>
          <DatePicker
            id="auditEndDate"
            dateFormat="DD/MM/YYYY"
            className="rounded-small margin-bottom-0"
            locale={momentLocale}
            timeFormat={false}
            value={endDate}
            onChange={(value) => setEndDate(value)}
            inputProps={{ placeholder: t("common.placeholder.selectDate") }}
          />
        </div>

        <div className="display-flex flex-column width-100pct-xs">
          <span className="display-none display-block-sm margin-bottom-5" aria-hidden="true">
            &nbsp;
          </span>
          <Button
            className="bg-primary text-color-white rounded-small height-35 width-100pct-xs"
            onClick={handleApplyFilter}
          >
            {t("settings.audit.button.applyFilter")}
          </Button>
        </div>
      </div>

      <div className="table-responsive margin-top-20">
        <table className="table table-layout-fixed table-bordered table-sticky table-head-filled min-width-500">
          <colgroup>
            {COLUMNS.map((column) => (
              <TableCol key={column} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <TableHead key={column} label={columnLabels[column]} column={column} />
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: PAGE_SIZE }, (_, rowIndex) => (
                <tr key={`audit-skeleton-${rowIndex}`}>
                  {COLUMNS.map((column) => (
                    <td key={column} data-field={columnLabels[column]}>
                      {column === "history" ? (
                        <ContentLoader
                          className="display-block"
                          height={SKELETON_ACTION_HEIGHT}
                          width={SKELETON_ACTION_WIDTH}
                        />
                      ) : (
                        <ContentLoader
                          className="display-block"
                          height={SKELETON_CELL_HEIGHT}
                          width={SKELETON_CELL_WIDTHS[column]}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading &&
              rows.map((row) => (
                <tr key={row.transactionId} data-id={row.transactionId}>
                  <td data-field={columnLabels.module}>
                    <span>{MODULE_LABEL_KEY[row.module] ? t(MODULE_LABEL_KEY[row.module]) : row.module}</span>
                  </td>
                  <td data-field={columnLabels.category}>
                    <span>{row.categoryName || "-"}</span>
                  </td>
                  <td data-field={columnLabels.createdAt}>
                    <span>{formatDate(row.createdAt)}</span>
                  </td>
                  <td data-field={columnLabels.updatedAt}>
                    <span>{formatDate(row.updatedAt)}</span>
                  </td>
                  <td data-field={columnLabels.status}>
                    <div className="display-flex flex-wrap align-items-center gap-5">
                      {row.status && (
                        <span className={STATUS_BADGE_CLASS[resolveTransactionStatus(row.status)]}>
                          {t(getTransactionStatusLabelKey(row.module, row.status))}
                        </span>
                      )}
                      {!row.status && !row.deleted && <span>-</span>}
                      {row.deleted && <span className="badge badge-danger">{t("settings.audit.status.deleted")}</span>}
                    </div>
                  </td>
                  <td data-field={columnLabels.history}>
                    <button type="button" className="btn btn-default" onClick={() => setSelectedAudit(row)}>
                      {t("settings.audit.button.history")}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {!isLoading && hasError && (
          <div className="text-center text-color-danger padding-15">{t("settings.audit.message.loadError")}</div>
        )}

        {!isLoading && !hasError && rows.length === 0 && (
          <div className="text-center text-color-gray padding-15">{t("common.label.noData")}</div>
        )}
      </div>

      <div className="display-flex flex-column align-items-center margin-top-15">
        <div className="display-flex align-items-center justify-content-center gap-10">
          <Button
            iconOnly
            variant={Button.VARIANT_LINK}
            iconName="rioglyph-chevron-left"
            className="text-color-gray text-size-14"
            disabled={page === 0 || isLoading}
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          />
          <span className="text-color-gray text-medium text-size-14">
            {page + 1}/{totalPages}
          </span>
          <Button
            iconOnly
            variant={Button.VARIANT_LINK}
            iconName="rioglyph-chevron-right"
            className="text-color-gray text-size-14"
            disabled={page >= totalPages - 1 || isLoading}
            onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
          />
        </div>
        <div className="progress height-2 bg-lighter width-250 margin-bottom-10">
          <div
            className={`progress-bar ${
              progressWidth === "100%" ? "" : "border border-right-only border-color-white border-width-2"
            }`}
            style={{ width: progressWidth }}
          />
        </div>
      </div>

      <DialogAudit
        show={!!selectedAudit}
        onClose={() => setSelectedAudit(null)}
        transactionId={selectedAudit?.transactionId}
        entityLabel={
          selectedAudit && MODULE_LABEL_KEY[selectedAudit.module]
            ? t(MODULE_LABEL_KEY[selectedAudit.module])
            : t("settings.audit.entityLabel")
        }
      />
    </div>
  );
};

export default AuditPage;
